import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import MentalHealthRadarChart from '../components/common/MentalHealthRadarChart';
import { RadarChartProvider } from '../context/shared/RadarChartContext';
import { Patient } from '../shared/types/src';
import apiService from '../shared/services/src/apiService';
import ModifyPatientModal from '../components/therapist/ModifyPatientModal';

interface PatientDetails {
  id: string;
  basicInfo: {
    name: string;
    age: number;
    gender: string;
    email: string;
    phone: string;
    emergencyContact: string;
    emergencyPhone: string;
    dateOfBirth: string;
    pronouns: string;
  };
  medicalInfo: {
    birthDate: string;
    medications: string[];
    allergies: string[];
    conditions: string[];
    lastPhysical: string;
    insurance: string;
    primaryCarePhysician: string;
  };
  clinicalInfo: {
    diagnosis: string[];
    treatmentPlan: string;
    goals: string[];
    riskFactors: string[];
    lastSession: string;
    nextSession: string;
    therapist: string;
    progress: number;
    status: string;
  };
  notes: Array<{
    id: string;
    type: 'session' | 'clinical' | 'general';
    title: string;
    content: string;
    timestamp: string;
    therapist: string;
  }>;
  alerts: Array<{
    id: string;
    type: string;
    severity: string;
    message: string;
    timestamp: string;
    resolved: boolean;
  }>;
  insights: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    timestamp: string;
    confidence: number;
  }>;
}

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth: string): number => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

const TherapistPatientDetails: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  
  const [patient, setPatient] = useState<Patient | null>(null);
  const [patientDetails, setPatientDetails] = useState<PatientDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newNote, setNewNote] = useState({ type: 'session' as const, title: '', content: '' });
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPatient = async () => {
      if (patientId) {
        setIsLoading(true);
        try {
          setError(null); // Clear any previous errors
          
          // Load patient data from API
          const patientData = await apiService.getPatient(patientId);
          
          // DEBUG: Log the received data
          console.log('🔧 [PATIENT DETAILS] Received patient data:', patientData);
          console.log('🔧 [PATIENT DETAILS] Patient ID:', patientId);
          console.log('🔧 [PATIENT DETAILS] Patient UID:', patientData?.uid);
          console.log('🔧 [PATIENT DETAILS] Patient displayName:', patientData?.displayName);
          console.log('🔧 [PATIENT DETAILS] Patient dateOfBirth:', patientData?.dateOfBirth);
          console.log('🔧 [PATIENT DETAILS] Patient gender:', patientData?.gender);
          
          if (!patientData || !patientData.uid) {
            setError('Patient not found or invalid data received from server.');
            setIsLoading(false);
            return;
          }
          
          setPatient(patientData);
          
          // Load additional patient details
          const [notes, alerts, insights] = await Promise.all([
            apiService.getTherapistNotes(patientId).catch(() => []),
            apiService.getPatientAlerts(patientId).catch(() => []),
            apiService.getPatientInsights(patientId).catch(() => [])
          ]);
          
          // Create enhanced patient details
          const enhanced: PatientDetails = {
            id: patientData.uid,
            basicInfo: {
              name: patientData.displayName || 'Unknown',
              age: patientData.dateOfBirth ? calculateAge(patientData.dateOfBirth) : 0,
              gender: patientData.gender || 'Not specified',
              email: patientData.email,
              phone: patientData.phone || 'Not provided',
              emergencyContact: patientData.emergencyContact || 'Not provided',
              emergencyPhone: patientData.emergencyPhone || 'Not provided',
              dateOfBirth: patientData.dateOfBirth || '',
              pronouns: patientData.pronouns || 'Not specified'
            },
            medicalInfo: {
              birthDate: patientData.dateOfBirth ? new Date(patientData.dateOfBirth).toLocaleDateString() : 'Not provided',
              medications: patientData.medications || [],
              allergies: patientData.allergies || [],
              conditions: patientData.conditions || [],
              lastPhysical: patientData.lastPhysical || 'Not provided',
              insurance: patientData.insurance || 'Not provided',
              primaryCarePhysician: patientData.primaryCarePhysician || 'Not provided'
            },
            clinicalInfo: {
              diagnosis: patientData.diagnosis || [],
              treatmentPlan: patientData.treatmentPlan || 'Not specified',
              goals: patientData.goals || [],
              riskFactors: patientData.riskFactors || [],
              lastSession: patientData.lastSession || 'Not scheduled',
              nextSession: patientData.nextSession || 'Not scheduled',
              therapist: user?.displayName || 'Current Therapist',
              progress: patientData.progress || 0,
              status: patientData.status || 'Active'
            },
            notes: notes || [],
            alerts: alerts || [],
            insights: insights || [
              {
                id: '1',
                type: 'breakthrough',
                title: 'Positive Progress in Emotional Regulation',
                description: 'Patient has shown significant improvement in managing anxiety triggers over the past week.',
                timestamp: new Date().toISOString(),
                confidence: 0.85
              },
              {
                id: '2',
                type: 'trend',
                title: 'Consistent Sleep Pattern Improvement',
                description: 'Sleep quality has been steadily improving with the new routine.',
                timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                confidence: 0.72
              },
              {
                id: '3',
                type: 'concern',
                title: 'Increased Stress During Work Hours',
                description: 'Patient reports higher stress levels during work hours, may need additional coping strategies.',
                timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                confidence: 0.68
              }
            ]
          };
          
          setPatientDetails(enhanced);
        } catch (error) {
          console.error('Failed to load patient details:', error);
          setError('Failed to load patient data. Please check your connection and try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPatient();
  }, [patientId, user]);

  const handleSaveNote = async () => {
    if (!patientId || !newNote.title || !newNote.content) return;
    
    try {
      await apiService.createTherapistNote({
        patient_id: patientId,
        type: newNote.type,
        title: newNote.title,
        content: newNote.content,
        therapist_id: user?.uid || ''
      });
      
      // Refresh notes
      const notes = await apiService.getTherapistNotes(patientId);
      setPatientDetails(prev => prev ? { ...prev, notes } : null);
      
      setNewNote({ type: 'session', title: '', content: '' });
      setShowNoteForm(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const handleUpdatePatient = async () => {
    if (!patient || !patientDetails) return;
    
    try {
      // Update patient data
      await apiService.updatePatient(patient.uid, {
        displayName: patientDetails.basicInfo.name,
        email: patientDetails.basicInfo.email,
        dateOfBirth: patientDetails.basicInfo.dateOfBirth,
        // Add other fields as needed
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update patient:', error);
    }
  };

  const handlePatientUpdated = () => {
    // Reload patient data after update
    if (patientId) {
      loadPatient();
    }
    setShowModifyModal(false);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading patient details...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h2 className="text-lg font-bold text-red-800 mb-2">Unable to Load Patient Data</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-y-2">
              <button 
                onClick={() => window.location.reload()} 
                className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Retry
              </button>
              <button 
                onClick={() => navigate(-1)} 
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!patient || !patientDetails) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Patient Not Found</h2>
          <button 
            onClick={() => navigate(-1)} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Patients
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div className="flex-1 min-w-0">
              <button 
                onClick={() => navigate(-1)} 
                className="mb-4 bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md px-4 py-2 font-medium flex items-center transition-colors"
              >
                <span>←</span>
                <span className="ml-2">Back to Patients</span>
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                {patientDetails.basicInfo.name}
              </h1>
              <p className="text-gray-600 mt-2">
                {patientDetails.basicInfo.age} years old • {patientDetails.clinicalInfo.status}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {patientDetails.clinicalInfo.status}
                </span>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {patientDetails.clinicalInfo.progress}% Progress
                </span>
              </div>
            </div>
            <div className="flex flex-col w-full sm:w-auto gap-2">
              <button 
                onClick={() => setShowModifyModal(true)}
                className="w-full px-3 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Edit Patient
              </button>
              <button 
                onClick={() => setShowNoteForm(true)}
                className="w-full px-3 py-2 text-sm sm:text-base bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Add Note
              </button>
              <button 
                onClick={() => navigate(`/chat?patient=${patientId}`)}
                className="w-full px-3 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                View Chat History
              </button>
            </div>
          </div>
        </div>

        {/* Patient Information Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
              Basic Information
            </h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Age:</span>
                <span className="ml-2 text-gray-900">{patientDetails.basicInfo.age} years old</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Gender:</span>
                <span className="ml-2 text-gray-900">{patientDetails.basicInfo.gender}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Pronouns:</span>
                <span className="ml-2 text-gray-900">{patientDetails.basicInfo.pronouns}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Email:</span>
                <span className="ml-2 text-gray-900">{patientDetails.basicInfo.email}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Phone:</span>
                <span className="ml-2 text-gray-900">{patientDetails.basicInfo.phone}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Emergency Contact:</span>
                <div className="ml-2 text-gray-900">
                  <div>{patientDetails.basicInfo.emergencyContact}</div>
                  <div className="text-sm">{patientDetails.basicInfo.emergencyPhone}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
              Medical Information
            </h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Birth Date:</span>
                <span className="ml-2 text-gray-900">{patientDetails.medicalInfo.birthDate}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Medications:</span>
                <div className="ml-2">
                  {patientDetails.medicalInfo.medications.length > 0 ? (
                    patientDetails.medicalInfo.medications.map((med, index) => (
                      <div key={index} className="text-gray-900 text-sm">• {med}</div>
                    ))
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Allergies:</span>
                <div className="ml-2">
                  {patientDetails.medicalInfo.allergies.length > 0 ? (
                    patientDetails.medicalInfo.allergies.map((allergy, index) => (
                      <div key={index} className="text-gray-900 text-sm">• {allergy}</div>
                    ))
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Conditions:</span>
                <div className="ml-2">
                  {patientDetails.medicalInfo.conditions.map((condition, index) => (
                    <div key={index} className="text-gray-900 text-sm">• {condition}</div>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Insurance:</span>
                <span className="ml-2 text-gray-900">{patientDetails.medicalInfo.insurance}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">PCP:</span>
                <span className="ml-2 text-gray-900">{patientDetails.medicalInfo.primaryCarePhysician}</span>
              </div>
            </div>
          </div>

          {/* Clinical Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
              Clinical Information
            </h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Diagnosis:</span>
                <div className="ml-2">
                  {patientDetails.clinicalInfo.diagnosis.map((diagnosis, index) => (
                    <div key={index} className="text-gray-900 text-sm">• {diagnosis}</div>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Treatment Plan:</span>
                <span className="ml-2 text-gray-900">{patientDetails.clinicalInfo.treatmentPlan}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Goals:</span>
                <div className="ml-2">
                  {patientDetails.clinicalInfo.goals.map((goal, index) => (
                    <div key={index} className="text-gray-900 text-sm">• {goal}</div>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Risk Factors:</span>
                <div className="ml-2">
                  {patientDetails.clinicalInfo.riskFactors.map((risk, index) => (
                    <div key={index} className="text-red-600 text-sm">• {risk}</div>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Last Session:</span>
                <span className="ml-2 text-gray-900">{patientDetails.clinicalInfo.lastSession}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Next Session:</span>
                <span className="ml-2 text-gray-900">{patientDetails.clinicalInfo.nextSession}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Therapist:</span>
                <span className="ml-2 text-gray-900">{patientDetails.clinicalInfo.therapist}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Emotional Radar Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
            Emotional Radar Chart
          </h2>
          <div className="h-96">
            <RadarChartProvider patientId={patientId}>
              <MentalHealthRadarChart />
            </RadarChartProvider>
          </div>
        </div>

        {/* Clinical Notes Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Clinical Notes</h2>
            <button 
              onClick={() => setShowNoteForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Add Note
            </button>
          </div>
          
          {/* Add Note Form */}
          {showNoteForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Note</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note Type</label>
                  <select 
                    value={newNote.type}
                    onChange={(e) => setNewNote({...newNote, type: e.target.value as 'session' | 'clinical' | 'general'})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="session">Session Note</option>
                    <option value="clinical">Clinical Note</option>
                    <option value="general">General Note</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input 
                    type="text"
                    value={newNote.title}
                    onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter note title..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <textarea 
                    value={newNote.content}
                    onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter note content..."
                  />
                </div>
                <div className="flex space-x-3">
                  <button 
                    onClick={handleSaveNote}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Save Note
                  </button>
                  <button 
                    onClick={() => setShowNoteForm(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Notes List */}
          <div className="space-y-4">
            {patientDetails.notes.length > 0 ? (
              patientDetails.notes.map((note) => (
                <div key={note.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{note.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      note.type === 'session' ? 'bg-blue-100 text-blue-800' :
                      note.type === 'clinical' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {note.type}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{note.content}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>By: {note.therapist}</span>
                    <span>{new Date(note.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No notes yet. Add a note to get started.</p>
            )}
          </div>
        </div>

        {/* Alerts */}
        {patientDetails.alerts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Alerts</h2>
            <div className="space-y-3">
              {patientDetails.alerts.filter(alert => !alert.resolved).map((alert) => (
                <div key={alert.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                  <span className={`w-3 h-3 rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'high' ? 'bg-orange-500' :
                    alert.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}></span>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{alert.type.replace('_', ' ')}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                        alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <button className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    Resolve
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Insights</h2>
          {patientDetails.insights.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {patientDetails.insights.slice(0, 4).map((insight) => (
                <div key={insight.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      insight.type === 'breakthrough' ? 'bg-green-100 text-green-800' :
                      insight.type === 'concern' ? 'bg-red-100 text-red-800' :
                      insight.type === 'trend' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {insight.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {Math.round(insight.confidence * 100)}% confidence
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">{insight.title}</h3>
                  <p className="text-sm text-gray-600">{insight.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(insight.timestamp).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">💡</span>
              </div>
              <p className="text-gray-600">No insights available yet</p>
              <p className="text-sm text-gray-500 mt-2">AI-generated insights will appear here as the patient engages with the platform</p>
            </div>
          )}
        </div>
      </div>

      {/* Modify Patient Modal */}
      <ModifyPatientModal
        isOpen={showModifyModal}
        onClose={() => setShowModifyModal(false)}
        onPatientUpdated={handlePatientUpdated}
        patient={patient}
      />
    </PageContainer>
  );
};

export default TherapistPatientDetails;
